import { useEffect, useMemo, useState, useCallback } from 'react';
import { AutoComplete, Badge, Dropdown, Space } from 'antd';
import { BellOutlined, DownOutlined, LogoutOutlined, MailOutlined, SearchOutlined } from '@ant-design/icons';
import UserAvatar from '../User/UserAvatar';
import { Link, useNavigate } from 'react-router-dom';
import { isAuthenticated, getJwtToken, getStoredUser, performLogout } from '../../utilities/authentication';
import { useDispatch, useSelector } from 'react-redux';
import config from '../../config';
import { fetchProfile } from '../../store/Profile/ProfileSlice';
import useTranslation from '../../localization/useTranslation';
import http from '../../store/services/http-common';

const NavBar = ({ compact = false }) => {
  const dispatch = useDispatch();
  const { theme } = useSelector((state) => state.theme);
  const { language } = useSelector((state) => state.language);
  const profileUser = useSelector((state) => state.profile?.user);
  const [user, setUser] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const token = getJwtToken();

  useEffect(() => {
    if (!isAuthenticated()) {
      setUser(null);
      return;
    }
    if (!profileUser && token) {
      dispatch(fetchProfile(token));
    }
    if (profileUser && typeof profileUser === 'object') {
      setUser(profileUser);
      return;
    }
    setUser(getStoredUser());
  }, [profileUser, token, dispatch]);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await http.get('/backoffice/notifications');
      const data = res?.data?.data;
      setNotifications(Array.isArray(data?.items) ? data.items : []);
      setUnreadCount(Number(data?.unread_count ?? 0));
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated()) {
      loadNotifications();
    }
  }, [loadNotifications]);

  const handleSearch = async (value) => {
    setSearchValue(value);
    if (!value || value.length < 2) {
      setSearchOptions([]);
      return;
    }
    try {
      const res = await http.get('/backoffice/search', { params: { q: value } });
      const data = res?.data?.data ?? {};
      const options = [
        ...(data.vendors ?? []).map((v) => ({ value: v.url, label: `[Vendor] ${v.label}`, type: 'vendor' })),
        ...(data.subscriptions ?? []).map((s) => ({ value: s.url, label: `[Sub] ${s.label}`, type: 'subscription' })),
      ];
      setSearchOptions(options);
    } catch {
      setSearchOptions([]);
    }
  };

  const handleMenuClick = ({ key }) => {
    if (key === 'logout') {
      performLogout(token, config.apiRootUrl);
      return;
    }
    if (key === 'profile') {
      navigate('/profile');
    }
  };

  const notificationItems = useMemo(() => (
    notifications.length
      ? notifications.map((n) => ({
          key: String(n.id),
          icon: <MailOutlined />,
          label: (
            <div onClick={async () => {
              await http.post(`/backoffice/notifications/${n.id}/read`);
              loadNotifications();
              if (n.data?.support_conversation_id) navigate(`/support?conversation=${n.data.support_conversation_id}`);
              else if (n.data?.demo_request_id) navigate('/demo-requests');
              else if (n.data?.plan_request_id) navigate('/plan-requests');
              else if (n.data?.subscription_id) navigate('/subscriptions');
            }}
            >
              <strong>{n.title}</strong>
              {n.body ? <div style={{ fontSize: 12 }}>{n.body}</div> : null}
            </div>
          ),
        }))
      : [{ key: 'empty', label: t('backoffice.navbar.notifications'), disabled: true }]
  ), [notifications, navigate, loadNotifications, t]);

  const menuItems = useMemo(() => ([
    { key: 'profile', label: t('profile.menu.profile') },
    { key: 'logout', icon: <LogoutOutlined />, label: t('profile.menu.logout') },
  ]), [t]);

  const isRtl = language === 'ar';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 16,
        width: '100%',
        minWidth: 0,
        justifyContent: 'space-between',
        // Header forces LTR for switch ordering; re-apply RTL here so profile sits at the far start in Arabic.
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      <AutoComplete
        style={{
          minWidth: compact ? 0 : 280,
          maxWidth: compact ? '100%' : 420,
          flex: 1,
          width: compact ? '100%' : undefined,
        }}
        options={searchOptions}
        onSearch={handleSearch}
        onSelect={(path) => navigate(path)}
        value={searchValue}
      >
        <input
          placeholder={t('backoffice.navbar.search')}
          style={{
            width: '100%',
            padding: '4px 11px',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            direction: isRtl ? 'rtl' : 'ltr',
            textAlign: isRtl ? 'right' : 'left',
          }}
        />
      </AutoComplete>

      <Space size={compact ? 10 : 16} style={{ flexShrink: 0 }}>
        <Dropdown menu={{ items: notificationItems }} trigger={['click']} placement={isRtl ? 'bottomLeft' : 'bottomRight'}>
          <Badge count={unreadCount} size="small">
            <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
          </Badge>
        </Dropdown>

        {isAuthenticated() ? (
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={['click']}
            placement={isRtl ? 'bottomLeft' : 'bottomRight'}
          >
            <span style={{ display: 'inline-block', cursor: 'pointer' }}>
              <Space
                style={{
                  color: theme === 'dark' ? '#ffffff' : '#000000',
                  fontWeight: 500,
                  direction: isRtl ? 'rtl' : 'ltr',
                }}
              >
                <UserAvatar user={typeof user === 'object' ? user : null} size={compact ? 26 : 28} />
                {!compact ? (
                  <span className="navbar-user-name">
                    {typeof user === 'string' ? user : user?.name ?? 'User'}
                  </span>
                ) : null}
                <DownOutlined />
              </Space>
            </span>
          </Dropdown>
        ) : (
          <Link to="/" style={{ color: theme === 'dark' ? '#ffffff' : '#000000' }}>Login</Link>
        )}
      </Space>
    </div>
  );
};

export default NavBar;
